import { useSession } from '@repo/api-client/react';
import { Loader2 } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { MaintenanceScreen } from '@/app/MaintenanceScreen';

interface GateProps {
  children: ReactNode;
}

/**
 * Authenticated app section. Redirects unauthenticated users to
 * `/sign-in?next=...` so they bounce back into the page they tried
 * to load after signing in.
 *
 * Email-verification gate happens one layer down (in the layout) so
 * the user can still hit the verify-required screen without losing
 * their place — the auth gate's job is just "are they signed in".
 */
export function AuthGate({ children }: GateProps) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return <BootSpinner />;
  }
  if (status === 'error') {
    return <MaintenanceScreen />;
  }
  if (status !== 'authenticated') {
    const next = `${location.pathname}${location.search}`;
    const to = next === '/' ? '/sign-in' : `/sign-in?next=${encodeURIComponent(next)}`;
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

/**
 * Inverse gate for `/sign-in` and friends. Sends already-signed-in
 * users straight back into the app so they don't get a confusing
 * "sign in again" form on the URL bar of an authenticated session.
 */
export function AnonymousOnlyGate({ children }: GateProps) {
  const { status, emailVerified } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return <BootSpinner />;
  }
  if (status === 'error') {
    return <MaintenanceScreen />;
  }
  if (status === 'authenticated') {
    // Honour `?next=` if it lives inside our app (no open redirects).
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const target = next && next.startsWith('/') ? next : '/';
    if (emailVerified === false) {
      return <Navigate to="/verify-required" replace />;
    }
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

export function BootSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}
